import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useUser } from "../shared/UserContext";
import { useClassroomMode } from "../shared/classroomMode";
import "./ClassroomModeTip.css";

// A one-time coach-mark on the Sign-Out screen that teaches Classroom Mode —
// the feature is easy to miss (the PIN lives in Settings and the lock is a
// keyboard shortcut / button). It meets the teacher exactly where a shared
// device gets handed to students. Dismissal persists per-uid so it never nags.
function dismissKey(uid) {
  return `classboon_classroom_tip_dismissed_${uid}`;
}

function ClassroomModeTip() {
  const { uid, classroomPin } = useUser();
  const { classroomMode, enterClassroomMode } = useClassroomMode();
  const navigate = useNavigate();

  const [dismissed, setDismissed] = useState(() => {
    if (!uid) return true;
    try {
      return localStorage.getItem(dismissKey(uid)) === "1";
    } catch {
      return false;
    }
  });

  // Nothing to show: not signed in, already locked down, or already dismissed.
  if (!uid || classroomMode || dismissed) return null;

  const hasPin = !!classroomPin;

  const dismiss = () => {
    try { localStorage.setItem(dismissKey(uid), "1"); } catch { /* ignore */ }
    setDismissed(true);
  };

  const goToSettings = () => navigate("/settings", { state: { tab: "classroom" } });

  const turnOn = () => {
    if (window.confirm("Turn on Classroom Mode? This device locks to kid-safe screens and needs your PIN to exit.")) {
      enterClassroomMode();
    }
  };

  return (
    <div className="classroom-tip" role="note">
      <button className="classroom-tip-close" onClick={dismiss} aria-label="Dismiss">×</button>
      <div className="classroom-tip-icon" aria-hidden="true">🔒</div>
      <div className="classroom-tip-body">
        <h3>Sharing this device with students?</h3>
        <p>
          <strong>Classroom Mode</strong> locks this device to the student-safe screens —
          Sign-Out, Attendance, and Library — so kids can't reach Settings, rosters,
          Emergency, or sign you out. You set a 4-digit PIN to turn it off again.
        </p>
        <div className="classroom-tip-actions">
          {hasPin ? (
            <>
              <button className="classroom-tip-primary" onClick={turnOn}>
                Turn on Classroom Mode
              </button>
              <button className="classroom-tip-secondary" onClick={goToSettings}>
                Change PIN
              </button>
              <span className="classroom-tip-hint">
                or press Ctrl/⌘ + Shift + L
              </span>
            </>
          ) : (
            <>
              <button className="classroom-tip-primary" onClick={goToSettings}>
                Set a PIN to get started
              </button>
              <span className="classroom-tip-hint">
                Settings → Classroom
              </span>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default ClassroomModeTip;
